import components from '../config';
import { useContext } from '@nuxtjs/composition-api';
import { v4 as uuidv4 } from 'uuid';

const PORTFOLIO_ROW_LIMIT = 10;
const PORTFOLIO_SHOW_MORE_LABEL = 'Portfolio click to expand';
const PORTFOLIO_SHOW_LESS_LABEL = 'Portfolio click to collapse';

const translatePortfolioLabel = (translate, label) => {
  const translated = typeof translate === 'function' ? translate(label) : label;
  return String(translated || label);
};

const getPortfolioRows = (table) => {
  const rows = [];

  if (table.tBodies?.length) {
    Array.from(table.tBodies).forEach((tbody) => {
      rows.push(...Array.from(tbody.rows));
    });
  }

  if (rows.length) {
    return rows;
  }

  return Array.from(table.rows || []).filter((row) => row.parentElement?.tagName?.toLowerCase() !== 'thead');
};

const setPortfolioTableState = (table, expanded, translate) => {
  const rows = getPortfolioRows(table);

  rows.forEach((row, index) => {
    row.hidden = index >= PORTFOLIO_ROW_LIMIT && !expanded;
  });

  const button = table.nextElementSibling?.matches?.('.portfolio-toggle')
    ? table.nextElementSibling.querySelector('.portfolio-toggle-button')
    : null;

  if (button) {
    const label = translatePortfolioLabel(translate, expanded ? PORTFOLIO_SHOW_LESS_LABEL : PORTFOLIO_SHOW_MORE_LABEL);
    const ariaExpanded = expanded ? 'true' : 'false';

    if (button.textContent !== label) {
      button.textContent = label;
    }

    if (button.getAttribute('aria-expanded') !== ariaExpanded) {
      button.setAttribute('aria-expanded', ariaExpanded);
    }
  }

  const expandedState = expanded ? 'true' : 'false';
  if (table.getAttribute('data-portfolio-expanded') !== expandedState) {
    table.setAttribute('data-portfolio-expanded', expandedState);
  }
};

const initPortfolioTables = (htmlBody, translate) => {
  if (typeof htmlBody.querySelectorAll !== 'function') return;

  htmlBody.querySelectorAll('table.portfolio').forEach((table) => {
    const rows = getPortfolioRows(table);
    if (rows.length <= PORTFOLIO_ROW_LIMIT) return;

    let toggle = table.nextElementSibling?.matches?.('.portfolio-toggle')
      ? table.nextElementSibling
      : null;

    if (!toggle) {
      toggle = document.createElement('div');
      toggle.className = 'portfolio-toggle';
      table.insertAdjacentElement('afterend', toggle);
    }

    let button = toggle.querySelector('.portfolio-toggle-button');
    if (!button) {
      button = document.createElement('button');
      button.type = 'button';
      button.className = 'portfolio-toggle-button tertiary-button default';
      toggle.appendChild(button);
    }

    setPortfolioTableState(table, table.getAttribute('data-portfolio-expanded') === 'true', translate);
  });
};

export const PBHelper = () => ({
  createDynamicBackgroundImage: (src) => {
    const UUID = uuidv4();
    const imageId = UUID.split('-').pop();
    return {
      className: 'background-image-' + imageId,
      style: '.background-image-' + imageId + ' {background-image: url(' + src + ');}'
    };
  },
  getComponentName: (data) => {
    const component = components().find((n) => n.tag === data.attr?.['data-content-type']);

    return component ? component.name : 'ElementsHtmlCode';
  },
  getProductUrlKeys: (content) => {
    const urlKeys = content.match(/(?:(https|http):\/\/)(.*?)(?=")/g).filter((i) => /\.html/.test(i)).map((i) => i.match(/(?:\/).*?(?=.html)/g)?.[0]);
    return [...new Set(urlKeys)];
  },
  convertMagentoLink: (content) => {
    const context = useContext();
    const { magentoBaseUrl } = context.$vsf.$magento.config;
    // convert background image link
    content = ((content) => {
      return content.replace(/(?:data-background-images=").*?(?:")/g, function (x) {
        const url = x.match(/((https|http|ftp|rtsp|mms)?:\/\/).*?(bmp|jpg|png|gif|raw|webp|jpeg)/)?.[0] ?? '';
        return 'data-background-images="' + url + '"';
      });
    })(content);
    // convert cms page link
    content = ((content) => {
      return content.replace(/(?:href=").*?(?:")/g, function (x) {
        const regex = new RegExp(/media\/wysiwyg|media\/cms/);
        return regex.test(x)?x:x.replace(magentoBaseUrl, '');
      });
    })(content);
    return content;
  },
  bindGtmEvent: (selector, callback) => {
    document.querySelector('#html-body').addEventListener('click', function(evt) {
      if (evt.target.matches(selector)) {
        callback(evt.target);
      }
    }, true); // Use Capturing
  },
  bindFaqToggleEvent: () => {
    const htmlBody = document.querySelector('#html-body');
    if (!htmlBody) return;

    htmlBody.addEventListener('click', function(evt) {
      const question = evt.target?.closest?.('.faq-wrap .faq-question');
      const faqItem = question?.closest?.('.faq-item');

      if (faqItem) {
        const isActive = faqItem.classList.contains('active');
        htmlBody.querySelectorAll('.faq-wrap .faq-item.active').forEach((item) => {
          if (item !== faqItem) {
            item.classList.remove('active');
          }
        });
        faqItem.classList.toggle('active', !isActive);
      }
    }, true);
  },
  bindAnchorScrollEvent: () => {
    document.querySelector('#html-body').addEventListener('click', function(evt) {
      const target = evt.target.closest('a.go-to-anchor');
      if (target) {
        evt.preventDefault();
        const anchorId = target.getAttribute('data-anchor');
        if (anchorId) {
          const element = document.getElementById(anchorId);
          if (element) {
            const header = document.querySelector('header');
            const headerRect = header ? header.getBoundingClientRect() : null;
            const headerOffset = headerRect ? headerRect.top + headerRect.height : 0;
            const offset = headerOffset + 10;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;
            window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
          }
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    }, true);
  },
  bindPortfolioTableToggleEvent: (translate) => {
    const htmlBody = document.querySelector('#html-body');
    if (!htmlBody) return () => {};

    initPortfolioTables(htmlBody, translate);

    if (htmlBody.getAttribute?.('data-portfolio-toggle-bound') === 'true') {
      return () => {};
    }

    const observer = typeof MutationObserver !== 'undefined' && typeof Node !== 'undefined' && htmlBody.nodeType === Node.ELEMENT_NODE
      ? new MutationObserver(() => initPortfolioTables(htmlBody, translate))
      : null;

    if (observer) {
      observer.observe(htmlBody, { childList: true, subtree: true });
    }

    const handleClick = function(evt) {
      const target = evt.target?.closest?.('.portfolio-toggle-button');
      const toggle = target?.closest?.('.portfolio-toggle');
      const table = toggle?.previousElementSibling;

      if (table?.matches?.('table.portfolio')) {
        const expanded = table.getAttribute('data-portfolio-expanded') === 'true';
        setPortfolioTableState(table, !expanded, translate);
      }
    };

    htmlBody.addEventListener('click', handleClick, true);

    htmlBody.setAttribute?.('data-portfolio-toggle-bound', 'true');

    return () => {
      observer?.disconnect();
      htmlBody.removeEventListener?.('click', handleClick, true);
      htmlBody.removeAttribute?.('data-portfolio-toggle-bound');
    };
  },
  checkVideoType: (link, str) => link.indexOf(str) > -1,
});

export default PBHelper;
